import React, { useState, useEffect } from "react";
import { getFeedbackInsights } from "../utils/bookingFeedbackUtils.js";

const FeedbackDashboard = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadInsights = async () => {
      try {
        const feedbackInsights = await getFeedbackInsights();
        setInsights(feedbackInsights);
      } catch (err) {
        setError("Failed to load feedback statistics");
        console.error("Error loading feedback insights:", err);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading feedback statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!insights || insights.total === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z" />
        </svg>
        <p className="text-lg font-medium">No feedback data available</p>
        <p className="text-sm">Feedback will appear here once users start submitting reviews.</p>
        <p className="text-xs text-gray-500 mt-2">
          Users will see a feedback popup after successfully booking a service.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {insights.alerts && insights.alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-semibold text-red-900 mb-2 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Alerts
          </h3>
          <ul className="text-sm text-red-700 space-y-1">
            {insights.alerts.map((alert, index) => (
              <li key={index}>• {alert}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Main Dashboard */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Booking Process Feedback</h2>
          <div className="text-sm text-gray-500">
            Last updated: {insights.lastUpdated?.toLocaleString()}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Feedback */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">{insights.total}</div>
            <div className="text-sm font-medium text-blue-800">Total Responses</div>
          </div>

          {/* Positive Feedback */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">{insights.positive}</div>
            <div className="text-sm font-medium text-green-800">Positive (👍)</div>
            <div className="text-xs text-green-600 mt-1">{insights.positiveRate}%</div>
          </div>

          {/* Negative Feedback */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">{insights.negative}</div>
            <div className="text-sm font-medium text-red-800">Negative (👎)</div>
            <div className="text-xs text-red-600 mt-1">{insights.negativeRate}%</div>
          </div>

          {/* Satisfaction Rate */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">{insights.positiveRate}%</div>
            <div className="text-sm font-medium text-purple-800">Satisfaction Rate</div>
          </div>
        </div>

        {/* Visual Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">User Satisfaction</span>
            <span className="text-sm text-gray-500">{insights.positive} of {insights.total} positive</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-green-400 to-green-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${insights.positiveRate}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {insights.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recommendations</h3>
          <div className="space-y-3">
            {insights.recommendations.map((rec, index) => (
              <div key={index} className={`p-4 rounded-lg border ${
                rec.type === 'success' ? 'bg-green-50 border-green-200' :
                rec.type === 'good' ? 'bg-blue-50 border-blue-200' :
                rec.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                rec.type === 'critical' ? 'bg-red-50 border-red-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <h4 className={`font-semibold mb-2 ${
                  rec.type === 'success' ? 'text-green-900' :
                  rec.type === 'good' ? 'text-blue-900' :
                  rec.type === 'warning' ? 'text-yellow-900' :
                  rec.type === 'critical' ? 'text-red-900' :
                  'text-gray-900'
                }`}>
                  {rec.title}
                </h4>
                <p className={`text-sm ${
                  rec.type === 'success' ? 'text-green-700' :
                  rec.type === 'good' ? 'text-blue-700' :
                  rec.type === 'warning' ? 'text-yellow-700' :
                  rec.type === 'critical' ? 'text-red-700' :
                  'text-gray-700'
                }`}>
                  {rec.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Breakdown */}
      {insights.serviceBreakdown && Object.keys(insights.serviceBreakdown).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Feedback by Service</h3>
          <div className="space-y-4">
            {Object.entries(insights.serviceBreakdown).map(([service, data]) => {
              const serviceRate = data.total > 0 ? ((data.positive / data.total) * 100).toFixed(1) : 0;
              return (
                <div key={service} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{service}</h4>
                    <span className="text-sm text-gray-500">{data.total} responses</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-green-600">👍 {data.positive}</span>
                    <span className="text-red-600">👎 {data.negative}</span>
                    <span className="text-purple-600">{serviceRate}% positive</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-gradient-to-r from-green-400 to-green-500 h-2 rounded-full"
                      style={{ width: `${serviceRate}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Feedback */}
      {insights.recentFeedback && insights.recentFeedback.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Feedback</h3>
          <div className="space-y-3">
            {insights.recentFeedback.map((feedback) => (
              <div key={feedback.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-xl">
                    {feedback.isPositive ? '👍' : '👎'}
                  </span>
                  <div>
                    <div className="font-medium text-gray-900">{feedback.service}</div>
                    <div className="text-sm text-gray-500">{feedback.platform}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {feedback.timestamp.toLocaleDateString()} {feedback.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDashboard;